'use client'

import { cx } from 'classix'
import { IconPlusLarge } from 'icons/components/IconPlusLarge'
import { useImageLoader } from 'media/hooks/image/useImageLoader/useImageLoader'
import Image, { type ImageProps } from 'next/image'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import type { Media } from 'pdp/src/types/FullProduct'
import { PdpActionEvents } from 'pdp/src/types/PdpActionEvents'
import type { ReactNode } from 'react'
import { CustomCursor } from 'utils/CustomCursor/CustomCursor'
import { useZoom } from '../Zoom/contexts/ZoomProvider'
import styles from './ZoomableImage.module.css'

type ZoomImageItem = Omit<ImageProps, 'src' | 'alt'> & {
	readonly label?: string
	readonly media: Media
	readonly dataTestid?: string
	readonly tabIndex?: number
}

export function ZoomableImage({
	media,
	dataTestid,
	className,
	label,
	tabIndex,
	priority,
	...imageProps
}: ZoomImageItem): ReactNode {
	const { alt, src } = media
	const { setZoomImage, zoomDialogRef } = useZoom()
	const { sendPdpActionEvent } = useAnalytics()
	const imageLoader = useImageLoader()

	const handleClick = (): void => {
		setZoomImage(media)
		zoomDialogRef.current?.show()
		sendPdpActionEvent(PdpActionEvents.ZOOM_OPEN)
	}

	return (
		<button
			type='button'
			lang='en'
			aria-label={label}
			className={cx(styles.imageItemButton)}
			data-testid={dataTestid}
			onClick={handleClick}
			tabIndex={tabIndex}
		>
			<CustomCursor
				cursorContent={
					<div className={styles.customCursorCotainer}>
						<IconPlusLarge />
					</div>
				}
			>
				<Image
					loader={imageLoader}
					className={cx(className)}
					priority={priority}
					fetchPriority={priority ? 'high' : 'auto'}
					{...imageProps}
					alt={alt}
					src={src}
				/>
			</CustomCursor>
		</button>
	)
}
